/* eslint-disable react/no-unescaped-entities */
/**
 * OwnerDesk V1 · Dashboard.
 *
 * Landing after signup/login for owners. Lists all cases owned by the
 * authenticated owner, most-recent first. Each card shows the address,
 * last valuation summary, current stage and (when assigned) the PA.
 *
 * Phase 2 will add: case detail page with editable property fields +
 * re-run valuation with side-by-side comparison, and a proper PA card
 * with contact + hand-off status.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, Plus, LogOut } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import PaCard from "@/components/owner/PaCard";
import AnonLinkPrompt from "@/components/owner/AnonLinkPrompt";
import { stageLabel } from "@/lib/enumLabels";

export default function OwnerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [state, setState] = React.useState({ loading: true, cases: [], err: null });
  const [saved, setSaved] = React.useState([]);

  const loadSaved = React.useCallback(async () => {
    try {
      const { listSavedAnalyses } = await import("@/lib/vg2SavedAnalyses");
      const r = await listSavedAnalyses();
      setSaved(r.saved || []);
    } catch (_) { /* non-blocking */ }
  }, []);

  const loadCases = React.useCallback(async () => {
    try {
      const r = await api.get("/v2/owner/cases");
      setState({ loading: false, cases: r.data.cases || [], err: null });
    } catch (e) {
      setState({ loading: false, cases: [], err: e?.response?.data?.detail || "Errore caricamento casi" });
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/v2/owner/cases");
        if (cancelled) return;
        setState({ loading: false, cases: r.data.cases || [], err: null });
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, cases: [], err: e?.response?.data?.detail || "Errore caricamento casi" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => { loadSaved(); }, [loadSaved]);

  const doLogout = () => { logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <AnonLinkPrompt onLinked={loadCases} />
      <header className="bg-white border-b border-gray-200"
              data-testid="owner-dashboard-header">
        <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><Logo size={24} /></Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600" data-testid="owner-dashboard-hello">
              Ciao, <strong className="text-[#0A0A0A]">{user?.nome}</strong>
            </span>
            <button onClick={doLogout}
                    className="text-sm text-gray-500 hover:text-[#B40101]
                               flex items-center gap-1.5"
                    data-testid="owner-dashboard-logout">
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0A0A0A]"
                data-testid="owner-dashboard-title">
              Le tue analisi
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Tutti i casi che hai avviato con ValutaGenio, sempre accessibili.
            </p>
          </div>
          <Link to="/"
                className="vg-btn-primary flex items-center gap-2"
                data-testid="owner-dashboard-new-case">
            <Plus size={18} /> Nuova analisi
          </Link>
        </div>

        {state.loading && (
          <div className="text-center py-16" data-testid="owner-dashboard-loading">
            <Loader2 className="animate-spin mx-auto text-[#B40101]" size={28} />
            <p className="text-sm text-gray-500 mt-3">Sto caricando le tue analisi…</p>
          </div>
        )}

        {!state.loading && state.err && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-6"
               data-testid="owner-dashboard-error">
            <p className="text-red-800 font-semibold">{state.err}</p>
          </div>
        )}

        {!state.loading && !state.err && state.cases.length === 0 && (
          <EmptyState />
        )}

        {!state.loading && !state.err && state.cases.length > 0 && (
          <div className="space-y-4" data-testid="owner-dashboard-cases">
            {state.cases.map((c) => (
              <CaseCard key={c.case_id} c={c} />
            ))}
          </div>
        )}

        {saved.length > 0 && (
          <section className="mt-12" data-testid="owner-dashboard-saved">
            <h2 className="text-2xl font-bold text-[#0A0A0A] mb-1">Analisi VALUTA salvate</h2>
            <p className="text-sm text-gray-500 mb-5">
              Le valutazioni e gli approfondimenti che hai salvato, con il
              collegamento all'immobile e allo scenario di origine.
            </p>
            <div className="space-y-3">
              {saved.map((s) => (
                <SavedAnalysisCard key={s.id} s={s} onRemoved={loadSaved} />
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function SavedAnalysisCard({ s, onRemoved }) {
  const isDeep = !!s.scenario_key;
  const addr = s.address;
  const line = addr
    ? [addr.street, addr.house_number, addr.municipality].filter(Boolean).join(" ")
    : "Immobile";
  const val = s.valuation || {};
  const range = (val.absolute_value_low != null && val.absolute_value_high != null)
    ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val.absolute_value_low)
      + " – " +
      new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val.absolute_value_high)
    : null;
  const href = isDeep
    ? `/vg2/valuta/scenario/${s.case_id}/${s.analysis_id}/${s.scenario_key}`
    : `/proprietari/casi/${s.case_id}`;

  const remove = async (e) => {
    e.preventDefault();
    try {
      const { deleteSavedAnalysis } = await import("@/lib/vg2SavedAnalyses");
      await deleteSavedAnalysis(s.id);
      onRemoved && onRemoved();
    } catch (_) { /* ignore */ }
  };

  return (
    <div className="rounded-xl bg-white border border-gray-200 p-4 flex items-center justify-between gap-4"
         data-testid={`owner-saved-${s.id}`}>
      <Link to={href} className="min-w-0 flex-1 no-underline" data-testid={`owner-saved-open-${s.id}`}>
        <p className="text-xs uppercase tracking-[0.12em] text-gray-400 mb-0.5">
          {isDeep ? "Approfondimento scenario" : "Valutazione generale"}
        </p>
        <h3 className="text-base font-bold text-[#0A0A0A] truncate">
          {isDeep ? (s.scenario_title || s.scenario_key) : line}
        </h3>
        <p className="text-sm text-gray-500 truncate">
          {isDeep ? line : (range || "Apri la valutazione")}
        </p>
      </Link>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-sm font-semibold text-[#B40101]">
          {isDeep ? "Apri approfondimento →" : "Apri →"}
        </span>
        <button onClick={remove}
                className="text-xs text-gray-400 hover:text-[#B40101]"
                data-testid={`owner-saved-remove-${s.id}`}>
          Rimuovi
        </button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl bg-white border border-gray-200 p-12 text-center"
         data-testid="owner-dashboard-empty">
      <h3 className="text-xl font-bold text-[#0A0A0A]">Non hai ancora avviato analisi</h3>
      <p className="text-sm text-gray-500 mt-2 mb-6 max-w-md mx-auto">
        Parti da zero: raccontaci cosa vuoi fare della tua casa e in pochi minuti
        avrai una prima analisi.
      </p>
      <Link to="/" className="vg-btn-primary inline-flex items-center gap-2"
            data-testid="owner-dashboard-empty-cta">
        Inizia la prima analisi <ArrowRight size={18} />
      </Link>
    </div>
  );
}

function CaseCard({ c }) {
  const addr = c.address || {};
  // address può essere una stringa oppure un oggetto {address, street, municipality}.
  const streetLine = typeof addr === "string"
    ? addr : (addr.street || addr.address || addr.raw);
  const muni = typeof addr === "string" ? null : addr.municipality;
  const line1 = [streetLine, muni].filter(Boolean).join(" · ")
    || "Indirizzo non ancora inserito";
  const val = c.latest_valuation;
  const isSale = val && val.valuation_mode === "sale";
  // Valore centrale + fascia: preferisci il valore assoluto dell'immobile
  // (fascia della valutazione); ripiega sull'unitario €/m² se assente.
  const hasAbs = val && val.absolute_value_central != null;
  const centralVal = hasAbs ? val.absolute_value_central : (val && val.unit_value_central);
  const lowVal = hasAbs ? val.absolute_value_low : (val && val.unit_value_low);
  const highVal = hasAbs ? val.absolute_value_high : (val && val.unit_value_high);
  const valUnit = hasAbs ? "€" : (isSale ? "€/m²" : "€/m²·mese");
  const lastAnalysis = val && val.created_at
    ? new Date(val.created_at).toLocaleDateString("it-IT") : null;
  const pa = c.pa_assignment;

  const dest = `/proprietari/casi/${c.case_id}`;

  return (
    <Link
      to={dest}
      className="block rounded-2xl bg-white border border-gray-200 hover:border-[#B40101]/40
                 hover:shadow-sm transition-all p-6"
      data-testid={`owner-dashboard-case-${c.case_id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-[0.14em] text-gray-400 mb-1"
             data-testid={`owner-case-objective-${c.case_id}`}>
            {c.objective_label || (stageLabel(c.stage) ? `Stato · ${stageLabel(c.stage)}` : "Analisi in corso")}
          </p>
          <h3 className="text-lg font-bold text-[#0A0A0A] truncate"
              data-testid={`owner-case-address-${c.case_id}`}>{line1}</h3>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
            {lastAnalysis
              ? <span>Ultima analisi <strong>{lastAnalysis}</strong></span>
              : <span className="text-gray-400">Analisi non ancora eseguita</span>}
            {pa && pa.pa_id && (
              <span className="text-[#B40101]">
                Property Advisor: <strong>{pa.pa_name || pa.pa_id}</strong>
              </span>
            )}
          </div>
        </div>
        <ArrowRight className="text-gray-400 mt-1 flex-shrink-0" size={22} />
      </div>

      {val && (centralVal != null) && (
        <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-3 gap-6"
             data-testid={`owner-case-value-${c.case_id}`}>
          <ValMetric label="Valore centrale" value={fmtEur(centralVal)} unit={valUnit} />
          <ValMetric label="Fascia prudente" value={fmtEur(lowVal)} unit={valUnit} />
          <ValMetric label="Fascia alta" value={fmtEur(highVal)} unit={valUnit} />
        </div>
      )}
    </Link>
  );
}

function ValMetric({ label, value, unit }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.12em] text-gray-400 mb-1">{label}</p>
      <p className="text-lg font-bold text-[#0A0A0A]">{value}<span className="text-xs text-gray-500 font-normal ml-1">{unit}</span></p>
    </div>
  );
}

function fmtEur(v) {
  if (v == null) return "—";
  return "€ " + Math.round(v).toLocaleString("it-IT");
}
